import { inject, injectable } from 'inversify-props';
import { IAppConfig, IAppConfigId } from '@/features/shared/IAppConfig';
import { AxiosId } from '@/plugins/axios';
import { AxiosInstance } from 'axios';
import { ITripsApi } from '@/features/trips/ITripsApi';
import { MpcGetTripsResponseDto } from '@/features/trips/MpcGetTripsResponseDto';
import { MpcTripUpdateResponse } from '@/features/trips/MpcTripUpdateResponse';
import { TripUpdateRequest } from '@/features/trips/TripUpdateRequest';
import { PassingTimeUpdateRequest } from '@/features/trips/PassingTimeUpdateRequest';
import { MpcPassingTimeUpdateResponse } from '@/features/trips/MpcPassingTimeUpdateResponse';
import { BookingData, BookingNoShowPage, DeleteBookingNoShowRequest } from '@/features/trips/BookingNoShowModels';

@injectable()
export default class TripsApi implements ITripsApi {
    @inject(IAppConfigId)
    config!: IAppConfig;

    @inject(AxiosId)
    axios!: AxiosInstance;

    getTrips(lineNumber: string, calendarDay?: string): Promise<MpcGetTripsResponseDto> {
        return new Promise((resolve, reject) => {
            this.axios
                .get<MpcGetTripsResponseDto>('/api/trips/gettrips', {
                    params: {
                        lineNumber,
                        calendarDay: calendarDay,
                        token: this.config.token
                    }
                })
                .then(response => {
                    resolve(response.data);
                })
                .catch(exception => {
                    reject(exception);
                });
        });
    }

    createTrip(tripId: string, calendarDay: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const data: any = {
                tripId,
                calendarDay: calendarDay
            };

            this.axios
                .post<MpcTripUpdateResponse>('/api/trips/createtrip?token=' + this.config.token, data)
                .then(() => resolve())
                .catch(e => reject(e));
        });
    }

    updateTrip(tripUpdateRequest: TripUpdateRequest): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            this.axios
                .post<MpcTripUpdateResponse>('/api/trips/updatetrip?token=' + this.config.token, tripUpdateRequest)
                .then(() => resolve())
                .catch(e => reject(e));
        });
    }

    updatePassingTime(passingTimeUpdateRequest: PassingTimeUpdateRequest): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            this.axios
                .post<MpcPassingTimeUpdateResponse>('/api/trips/updatepassingtimes?token=' + this.config.token, passingTimeUpdateRequest)
                .then(() => resolve())
                .catch(e => reject(e));
        });
    }

    registerBookingNoShow(bookingData: BookingData): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            this.axios
                .post<MpcTripUpdateResponse>('/api/trips/RegisterBookingNoShow?token=' + this.config.token, bookingData)
                .then(() => resolve())
                .catch(e => reject(e));
        });
    }

    deleteBookingNoShow(request: DeleteBookingNoShowRequest): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            this.axios
                .delete<DeleteBookingNoShowRequest>(`/api/trips/DeleteBookingNoShow?token=${this.config.token}`, { data: request })
                .then(() => resolve())
                .catch(e => reject(e));
        });
    }

    getBookingNoShows(page: BookingNoShowPage, filters: any): Promise<void> {
        return new Promise((resolve, reject) => {
            this.axios
                .get<any>(`/api/trips/FindBookingNoShows`, {
                    params: {
                        'page.number': page.page,
                        'page.size': page.size,
                        token: this.config.token,
                        'filters.noShowType': filters.noShowType,
                        'filters.dateFrom': filters.dateFrom,
                        'filters.dateTo': filters.dateTo,
                        'filters.tripId': filters.tripId,
                        'filters.operatingDay': filters.operatingDay
                    }
                })
                .then(response => resolve(response.data))
                .catch(e => reject(e));
        });
    }

    completeTrip(tripId: string, operatingDay: string): Promise<any> {
        const data: any = {
            tripId,
            operatingDay: operatingDay
        };
        return new Promise<void>((resolve, reject) => {
            this.axios
                .post('/api/trips/completetrip?token=' + this.config.token, data)
                .then(() => resolve())
                .catch(e => reject(e));
        });
    }

    cancelTrip(tripId: string, operatingDay: string): Promise<any> {
        const data: any = {
            tripId,
            operatingDay: operatingDay
        };
        return new Promise<void>((resolve, reject) => {
            this.axios
                .post('/api/trips/canceltrip?token=' + this.config.token, data)
                .then(() => resolve())
                .catch(e => reject(e));
        });
    }
}
